/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Select from "react-select";
import { updateCustomer } from "../../api/mutations";
import {
  COUNTRY_OPTIONS,
  customStyles,
  filterCountryOption,
  getCountryName,
} from "../../constants/countries";
import { useLanguage } from "../../contexts/LanguageContext";
import { useStore } from "../../contexts/StoreContext";
import { getApiErrorReasonMessage } from "../../utils/apiErrorReasons";
import { customFieldRows } from "../../utils/customFields";
import { buildCustomerUpdatePayload } from "../../utils/customerPayload";
import { balanceUnit } from "./customerFormat";
import { CloseButton } from "./ui";
import { buttonClass, inputClass, labelClass } from "./styles";

const selectStyles = {
  ...customStyles,
  control: (base, state) => ({
    ...base,
    minHeight: "2.75rem",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#6366F1" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 1px #6366F1" : "none",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 60 }),
};

const DATE_VALUE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;

// The drawer renders an input for every field the store's forms define, so
// they are seeded with "" when the customer has none (custom_fields can be
// null). buildCustomerUpdatePayload drops seeded blanks again, so only real
// edits reach the API.
function seedCustomFields(customer, definitions) {
  const seeded = {};
  (definitions || []).forEach((definition) => {
    seeded[definition.field] = "";
  });
  return { ...customer, custom_fields: { ...seeded, ...(customer.custom_fields || {}) } };
}

// Edit a customer. Sends only what changed (utils/customerPayload.js): a save
// regenerates the wallet pass and pushes a notification to the customer's
// phone, and an untouched balance must not travel, or discount cards read it
// back as a purchase.
export default function CustomerEditDrawer({ open, customer, passType, onClose, onSaved }) {
  const { t, language } = useLanguage();
  const { selectedStore } = useStore();
  const queryClient = useQueryClient();
  const storeId = selectedStore?.id;
  // The record as it was loaded, kept untouched so the save can diff against
  // it; `editing` changes with every keystroke and with the seeding.
  const [original, setOriginal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && customer) {
      setOriginal({ ...customer });
      setEditing(seedCustomFields(customer, selectedStore?.custom_fields));
      setError(null);
    }
    // Re-seed only when a different customer is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer?.id]);

  const reset = () => {
    setEditing(null);
    setOriginal(null);
    setError(null);
  };

  const mutation = useMutation({
    mutationFn: updateCustomer,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["customers", storeId] });
      queryClient.invalidateQueries({ queryKey: ["customer", storeId] });
      reset();
      onSaved?.(data);
      onClose();
    },
    onError: (err) => {
      const data = err.response?.data;
      const listed = Array.isArray(data?.errors) ? data.errors.join(", ") : data?.errors;
      setError(getApiErrorReasonMessage(data, t) || listed || data?.error || t("customers.edit.error"));
    },
  });

  const close = () => {
    reset();
    onClose();
  };

  const set = (field) => (event) =>
    setEditing((prev) => (prev ? { ...prev, [field]: event.target.value } : prev));

  const handleSubmit = (event) => {
    event.preventDefault();
    // A save that already succeeded clears `editing`, but the panel stays
    // mounted through its exit transition and the Save button can still be
    // hit there. A second click lands here with nothing to submit.
    if (!editing || mutation.isPending) return;

    const payload = buildCustomerUpdatePayload(original, editing);

    // Nothing was edited. Saving anyway is not free: the request regenerates
    // the wallet pass and pushes a notification to the customer's phone.
    if (Object.keys(payload).length === 0) {
      close();
      return;
    }

    mutation.mutate({ storeId, customerId: editing.id, data: payload });
  };

  const unit = balanceUnit(passType);
  const fieldRows = customFieldRows(editing?.custom_fields, selectedStore?.custom_fields);
  const fieldDefinitions = Object.fromEntries(
    (selectedStore?.custom_fields || []).map((definition) => [definition.field, definition]),
  );
  const originalFields = original?.custom_fields || {};
  const countryDefault =
    COUNTRY_OPTIONS.find((option) => editing?.phone_country_code?.startsWith(option.value)) ||
    COUNTRY_OPTIONS.find((option) => option.value === "+1");

  return (
    <Dialog open={open} onClose={close} className="relative z-50">
      <div className="fixed inset-0 bg-gray-900/45" aria-hidden="true" />
      <div className="fixed inset-0 overflow-hidden">
        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-6 sm:pl-16">
          <DialogPanel
            transition
            className="pointer-events-auto w-screen max-w-[480px] transform transition duration-300 ease-in-out data-closed:translate-x-full"
          >
            <form onSubmit={handleSubmit} className="flex h-full flex-col overflow-y-auto overscroll-contain bg-white shadow-xl">
              <div className="flex items-center justify-between gap-3 px-6 pt-6">
                <DialogTitle as="h2" className="text-xl font-semibold text-gray-900">
                  {t("customers.edit.heading", { name: editing?.name || t("customers.unnamed") })}
                </DialogTitle>
                <CloseButton onClick={close} label={t("common.close")} />
              </div>

              <div className="flex flex-col gap-4 px-6 py-5">
                {error && (
                  <p role="alert" className="m-0 rounded-[10px] bg-red-50 px-3 py-2.5 text-sm text-red-700">
                    {error}
                  </p>
                )}

                <label className={labelClass}>
                  {t("customers.table.name")}
                  <input
                    type="text"
                    name="name"
                    autoComplete="off"
                    value={editing?.name || ""}
                    onChange={set("name")}
                    className={inputClass}
                  />
                </label>

                <label className={labelClass}>
                  {t("customers.table.email")}
                  <input
                    type="email"
                    name="email"
                    autoComplete="off"
                    spellCheck={false}
                    value={editing?.email || ""}
                    onChange={set("email")}
                    placeholder={t("customers.sendPass.emailPlaceholder")}
                    className={inputClass}
                  />
                </label>

                <div className="flex flex-col gap-1.5">
                  <span id="customer-phone-label" className="text-[13px] font-semibold text-gray-700">
                    {t("customers.table.phone")}
                  </span>
                  <div role="group" aria-labelledby="customer-phone-label" className="flex gap-2">
                    <div className="w-40 shrink-0">
                      {editing && (
                        <Select
                          key={editing.id}
                          options={COUNTRY_OPTIONS}
                          defaultValue={countryDefault}
                          styles={selectStyles}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          isSearchable
                          aria-label={t("customers.edit.countryCode")}
                          placeholder={t("public.form.countryPlaceholder")}
                          onChange={(option) =>
                            setEditing((prev) => ({ ...prev, phone_country_code: option.value }))
                          }
                          formatOptionLabel={(option, { context }) => (
                            <div className="flex items-center gap-2">
                              <span>{option.label.split(" ")[0]}</span>
                              <span>{option.value}</span>
                              {context === "menu" && (
                                <span className="truncate text-gray-500">
                                  {getCountryName(option, language)}
                                </span>
                              )}
                            </div>
                          )}
                          filterOption={(option, inputValue) =>
                            filterCountryOption(option, inputValue, language)
                          }
                        />
                      )}
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      inputMode="tel"
                      autoComplete="off"
                      aria-label={t("customers.edit.phoneNumber")}
                      value={editing?.phone || ""}
                      onChange={set("phone")}
                      className={`${inputClass} min-w-0 flex-1`}
                    />
                  </div>
                </div>

                <label className={labelClass}>
                  {t("customers.table.birthday")}
                  <input
                    type="date"
                    name="birthday"
                    value={editing?.birthday || ""}
                    onChange={set("birthday")}
                    className={inputClass}
                  />
                </label>

                <label className={labelClass}>
                  {t(`customers.units.${unit}.label`)}
                  <input
                    type="number"
                    name="balance"
                    inputMode="decimal"
                    step="any"
                    // An emptied input parses to NaN, which the payload reads
                    // as "no adjustment"; show it empty rather than as 0.
                    value={Number.isFinite(Number(editing?.point)) && editing?.point !== null && editing?.point !== "" ? Number(editing.point) : ""}
                    onChange={(event) =>
                      setEditing((prev) => ({ ...prev, point: parseFloat(event.target.value) }))
                    }
                    className={`${inputClass} tabular-nums`}
                  />
                  <span className="text-xs font-normal text-gray-600">
                    {t(`customers.edit.balanceHint.${unit}`)}
                  </span>
                </label>

                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>
                    {t("customers.expiry.label")}
                    {/* The stored date for this entry, never one recalculated
                        from the card's current rule: the two legitimately
                        differ, which is the point of setting it per customer. */}
                    <input
                      type="date"
                      name="expires_at"
                      aria-describedby="customer-expiry-help"
                      value={(editing?.expires_at || "").slice(0, 10)}
                      onChange={set("expires_at")}
                      className={inputClass}
                    />
                  </label>
                  <span id="customer-expiry-help" className="text-xs text-gray-600">
                    {editing?.expires_at ? t("customers.expiry.help") : t("customers.expiry.helpEmpty")}
                  </span>
                  {editing?.expires_at && (
                    <button
                      type="button"
                      onClick={() => setEditing((prev) => ({ ...prev, expires_at: "" }))}
                      className="min-h-8 self-start text-[13px] font-semibold text-indigo-700 hover:text-indigo-800"
                    >
                      {t("customers.expiry.clear")}
                    </button>
                  )}
                </div>

                <label className="flex cursor-pointer items-start gap-2.5 text-sm text-gray-900">
                  <input
                    type="checkbox"
                    name="is_marketing_opted_in"
                    checked={!!editing?.is_marketing_opted_in}
                    onChange={(event) =>
                      setEditing((prev) => ({ ...prev, is_marketing_opted_in: event.target.checked }))
                    }
                    className="mt-0.5 h-[18px] w-[18px] rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="font-medium">{t("customers.edit.marketing.label")}</span>
                    {/* Verified against the backend: the birthday reward and
                        the wallet passes' branch locations read this flag.
                        Email and SMS sends do not, so it promises neither. */}
                    <span className="text-xs text-gray-600">{t("customers.edit.marketing.help")}</span>
                  </span>
                </label>

                {fieldRows.length > 0 && (
                  <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
                    <span className="text-[13px] font-semibold text-gray-900">
                      {t("customers.edit.formFields")}
                    </span>
                    {fieldRows.map(({ key, label }) => {
                      const value = editing.custom_fields[key];
                      const text = Array.isArray(value) ? value.join(", ") : value ?? "";
                      // The input type comes from the field's definition, not
                      // the current value, so clearing a date does not turn it
                      // into a text box. A multiple-choice answer (an array)
                      // is shown but not edited here: typing would replace the
                      // array with a string the forms and export can't read.
                      const definition = fieldDefinitions[key];
                      const original = originalFields[key];
                      const isDate = definition
                        ? definition.field_type === "date"
                        : DATE_VALUE.test(String(original ?? ""));
                      const readOnly = Array.isArray(original) || definition?.field_type === "checkbox";
                      return (
                        <label key={key} className={labelClass}>
                          {label}
                          <input
                            type={isDate ? "date" : "text"}
                            autoComplete="off"
                            readOnly={readOnly}
                            aria-readonly={readOnly || undefined}
                            value={typeof text === "string" ? text : String(text)}
                            onChange={(event) =>
                              setEditing((prev) => ({
                                ...prev,
                                custom_fields: { ...prev.custom_fields, [key]: event.target.value },
                              }))
                            }
                            className={readOnly ? `${inputClass} bg-gray-50 text-gray-600` : inputClass}
                          />
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 mt-auto flex justify-end gap-2 border-t border-gray-200 bg-white px-6 py-4">
                <button type="button" onClick={close} className={buttonClass.secondary}>
                  {t("common.cancel")}
                </button>
                <button type="submit" disabled={mutation.isPending || !editing} className={buttonClass.primary}>
                  {mutation.isPending ? t("common.saving") : t("customers.edit.save")}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
